import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MapPin, Mail, Globe, Send, Loader2, Clock } from "lucide-react";
import { PageHeader } from "@/components/site/PageHeader";
import { Reveal } from "@/components/site/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { COMPANY, IMAGES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || form.message.trim().length < 5) {
      toast.error("Please fill in all fields", {
        description: "A short message (5+ characters) helps us help you.",
      });
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent — thank you!", {
        description: "Our team in Jodhpur will get back to you shortly.",
      });
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong", {
        description: "Please try again, or email us directly at " + COMPANY.email,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHeader
        eyebrow="Contact Us"
        title="Let's Talk Flight"
        subtitle="Questions about a product, a bulk order for your club or NCC unit, or a defence UAS enquiry — reach out and the team will respond."
        image={IMAGES.drone}
      />

      <section className="bg-aero-deep">
        <div className="mx-auto grid max-w-[1400px] gap-12 px-5 py-20 sm:px-8 sm:py-28 lg:grid-cols-[1.1fr_0.9fr] lg:gap-16">
          {/* Form */}
          <Reveal>
            <form
              onSubmit={handleSubmit}
              data-testid="contact-form"
              className="rounded-2xl border border-steel/20 bg-aero-surface p-8 sm:p-10"
            >
              <h2 className="font-heading text-3xl font-bold tracking-tight text-ink">
                Send us a message
              </h2>
              <p className="mt-2 font-body text-sm text-steel">
                We typically reply within one business day.
              </p>

              <div className="mt-8 space-y-6">
                <div>
                  <Label htmlFor="name" className="font-heading text-xs uppercase tracking-[0.18em] text-steel">
                    Full Name
                  </Label>
                  <Input
                    id="name"
                    data-testid="contact-name-input"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Your name"
                    className="mt-2 h-12 border-steel/30 bg-aero-deep text-ink placeholder:text-steel/60 focus-visible:ring-saffron"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="font-heading text-xs uppercase tracking-[0.18em] text-steel">
                    Email Address
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="contact-email-input"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@example.com"
                    className="mt-2 h-12 border-steel/30 bg-aero-deep text-ink placeholder:text-steel/60 focus-visible:ring-saffron"
                  />
                </div>
                <div>
                  <Label htmlFor="message" className="font-heading text-xs uppercase tracking-[0.18em] text-steel">
                    Message
                  </Label>
                  <Textarea
                    id="message"
                    data-testid="contact-message-input"
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Tell us about your requirement..."
                    rows={6}
                    className="mt-2 border-steel/30 bg-aero-deep text-ink placeholder:text-steel/60 focus-visible:ring-saffron"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit-btn"
                  className="group flex w-full items-center justify-center gap-2 rounded-full bg-saffron px-8 py-4 font-heading font-semibold text-white transition-all duration-300 hover:bg-saffron-soft disabled:opacity-60"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" /> Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                    </>
                  )}
                </button>
              </div>
            </form>
          </Reveal>

          {/* Info + Map */}
          <Reveal delay={0.1}>
            <div className="flex h-full flex-col gap-6">
              <div className="rounded-2xl border border-steel/20 bg-aero-navy p-8">
                <h3 className="font-heading text-xl font-semibold text-ink">
                  Reach us directly
                </h3>
                <ul className="mt-6 space-y-5 font-body text-sm text-steel">
                  <li className="flex gap-4">
                    <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-saffron" />
                    <div>
                      <p className="font-heading text-sm font-semibold text-ink">Address</p>
                      {COMPANY.address}
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <Mail className="mt-0.5 h-5 w-5 shrink-0 text-saffron" />
                    <div>
                      <p className="font-heading text-sm font-semibold text-ink">Email</p>
                      <a href={`mailto:${COMPANY.email}`} className="hover:text-ink" data-testid="contact-email-link">
                        {COMPANY.email}
                      </a>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <Globe className="mt-0.5 h-5 w-5 shrink-0 text-saffron" />
                    <div>
                      <p className="font-heading text-sm font-semibold text-ink">Website</p>
                      {COMPANY.website}
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <Clock className="mt-0.5 h-5 w-5 shrink-0 text-saffron" />
                    <div>
                      <p className="font-heading text-sm font-semibold text-ink">Hours</p>
                      Mon–Sat · 10:00 – 18:00 IST
                    </div>
                  </li>
                </ul>
              </div>

              <div className="flex-1 overflow-hidden rounded-2xl border border-steel/20" data-testid="contact-map">
                <iframe
                  title="Sharma Model Aero — Jodhpur location"
                  src="https://maps.google.com/maps?q=Jodhpur%2C%20Rajasthan&t=&z=12&ie=UTF8&iwloc=&output=embed"
                  className="h-full min-h-[280px] w-full grayscale"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
